import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import { Progress, Select, Spin } from 'antd'

import requests from '../../../axios/requests'
import citiesRus from '../../../data/citiesRussua.json'
import { setMyRegion } from '../../../../redux/actions/userActions'
import { setFlagFavorite, setFlagFilter } from '../../../../redux/actions/actions'
import {
  setActiveProfession,
  setActiveVacancy,
  setFlagLoadingVacancy,
  setVacancycData,
} from '../../../../redux/actions/searchProfessionActions'
import useTrajectory from '../../SkillSet/gen/useTrajectory'
import { salaryAverage, salaryLabel, sameName, skillStatus } from '../../SkillSet/gen/genUtils'
import { SALARY_ACTIONS_ID } from '../../SkillSet/gen/SalaryPanel'
import { flyToPlan } from '../../SkillSet/gen/fly'
import s from '../../SkillSet/gen/gen.module.scss'
import p from './ProfReview.module.scss'

const PAGE = 12
const LIMIT_OTHERS = 4

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
)
const PinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
)

const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v6M12 7.5v.5" />
  </svg>
)
// цвет круга совпадения — как в прежнем списке профессий (defaultData.calcColorPercentProgressProfession)
const matchColor = (pct) => (pct >= 66 ? '#30E0A1' : pct >= 33 ? '#FF5937' : '#FF3434')

// Город для вакансий (как было в «Зарплатных возможностях»).
const RegionSelect = () => {
  const dispatch = useDispatch()
  const [cities, setCities] = useState([])
  const [typed, setTyped] = useState('')
  const saved = (() => {
    try {
      return JSON.parse(localStorage.getItem('region_new') || 'null')
    } catch (e) {
      return null
    }
  })()
  const load = () => {
    if (cities.length) return
    setCities(citiesRus.areas.map((c) => ({ name: c.name, id: c.region, areas: c.areas })))
  }
  const change = (value) => {
    const city = JSON.parse(value)
    const obj = { name: typed || city.name, id: city.id, areas: city.areas }
    localStorage.setItem('region_new', JSON.stringify(obj))
    dispatch(setMyRegion(obj))
  }
  return (
    <div className={p.region}>
      <span className={p.regionIcon}>
        <PinIcon />
      </span>
      <Select
        showSearch
        showArrow={false}
        placeholder="Город или регион"
        defaultValue={saved?.name || undefined}
        onChange={change}
        onSearch={setTyped}
        onDropdownVisibleChange={(open) => open && load()}
        aria-label="Город или регион"
        filterOption={(input, option) => {
          const city = JSON.parse(option.value)
          const q = input.toLowerCase()
          return city.name.toLowerCase().includes(q) || city.areas.some((a) => a.name.toLowerCase().includes(q))
        }}
      >
        {cities.map((c, i) => (
          <Select.Option key={i} value={JSON.stringify(c)}>
            {c.name}
          </Select.Option>
        ))}
      </Select>
    </div>
  )
}

// Чип навыка: не хватает (+ добавить) / в плане (клик убирает) / уже есть.
const SkillChip = ({ skill, kind, onToggle, small, label }) => {
  const cls = cn(s.chip, { [s.chipSm]: small })
  if (kind === 'have') return <span className={cn(cls, s.have)}>✓ {skill.name}</span>
  // навык с уже созданным курсом из плана не убрать — показываем без кнопки
  if (kind === 'plan' && label)
    return (
      <span className={cn(cls, s.inplan)} style={{ cursor: 'default' }} title="По навыку уже создан курс">
        ✓ {skill.name}
        {!small && ' · курс есть'}
      </span>
    )
  if (kind === 'plan')
    return (
      <button
        type="button"
        className={cn(cls, s.inplan)}
        title="Нажмите, чтобы убрать из плана"
        onClick={(e) => {
          e.stopPropagation()
          onToggle && onToggle(skill)
        }}
      >
        ✓ {skill.name}
        {!small && ' · в плане'}
      </button>
    )
  return (
    <button
      type="button"
      className={cn(cls, s.miss)}
      aria-label={`Добавить «${skill.name}» в учебный план`}
      data-skill-chip={skill.name}
      onClick={(e) => {
        e.stopPropagation()
        onToggle && onToggle(skill, e.currentTarget)
      }}
    >
      <span className={s.plus}>+</span>
      {skill.name}
    </button>
  )
}

const ProfReview = (props) => {
  const {
    displayListProfession,
    listProfessionOrigin,
    loadingProfession,
    currentPage,
    setCurrentPage,
    searchProfessionInput,
    setSearchProfessionInput,
    favoriteProfession,
    favoriteVacancy,
    handleChangeFavoriteProfession,
    handleChangeFavoriteVacancy,
    openProfessionInfo,
    changeIntro,
  } = props
  const dispatch = useDispatch()
  const { activeProfession, activeVacancy, vacancycData, flagLoadingVacancy } = useSelector(
    (state) => state.SearchProfession
  )
  const { userSkills, myRegion, favoritesSum, flagModalFavorite, flagModalFilter } = useSelector(
    (state) => state.Reducer
  )
  const trajectory = useTrajectory()
  const [q, setQ] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [flash, setFlash] = useState(0)
  const focusRef = useRef(null)

  // вакансии по профессии и региону: hh и trudvsem параллельно, hh показываем сразу (~1 с),
  // trudvsem дописываем, когда ответит (бывает 10+ с); лоадер — пока нет ни одной вакансии
  useEffect(() => {
    dispatch(setVacancycData(null))
    if (!activeProfession) {
      dispatch(setFlagLoadingVacancy(false))
      return
    }
    let alive = true
    let hh = []
    let tv = []
    const show = () => alive && dispatch(setVacancycData(hh.length || tv.length ? [...hh, ...tv] : null))
    dispatch(setFlagLoadingVacancy(true))
    const hhReq = requests.profs
      .get_hh_vacancy(activeProfession.professionTitle, myRegion?.name)
      .then((res) => {
        hh = res.data?.vacancies || []
        show()
      })
      .catch((err) => console.error(err))
    const tvReq = requests.profs
      .get_trudvsem_vacancy(activeProfession.professionTitle, myRegion?.id, false)
      .then((res) => {
        tv = res.data?.vacancies || []
        show()
      })
      .catch((err) => console.error(err))
    Promise.all([hhReq, tvReq]).then(() => alive && dispatch(setFlagLoadingVacancy(false)))
    return () => {
      alive = false
    }
  }, [activeProfession, myRegion])

  useEffect(
    () => () => {
      dispatch(setActiveVacancy(null))
      dispatch(setActiveProfession(null))
    },
    []
  )

  useEffect(() => {
    setShowAll(false)
    setQ('')
  }, [activeProfession])

  const vacancies = useMemo(() => (vacancycData || []).filter((v) => v && v.id), [vacancycData])
  const userHas = (name) => (userSkills || []).some((u) => sameName(u.name, name))
  const kindOf = (name) => (userHas(name) ? 'have' : trajectory.has(name) ? 'plan' : 'miss')
  const lockedInPlan = (name) => {
    const t = trajectory.list.find((x) => sameName(x.name, name))
    return t && skillStatus(t).kind === 'course'
  }
  const toggleSkill = (skill, el) => {
    if (trajectory.has(skill.name)) {
      if (!lockedInPlan(skill.name)) trajectory.remove(skill)
    } else {
      flyToPlan(el, skill.name)
      trajectory.add(skill)
    }
  }
  // «Добавить все недостающие»: навыки улетают в план по очереди
  const addAllMissing = (skills) => {
    const chips = focusRef.current ? Array.from(focusRef.current.querySelectorAll('[data-skill-chip]')) : []
    skills.forEach((sk, i) =>
      flyToPlan(
        chips.find((c) => c.getAttribute('data-skill-chip') === sk.name),
        sk.name,
        i * 120
      )
    )
    trajectory.addMany(skills)
  }
  const chipsFor = (skills) =>
    (skills || [])
      .filter((x) => x && x.name)
      .map((x) => ({ skill: x, kind: kindOf(x.name) }))
      .sort((a, b) => ['miss', 'plan', 'have'].indexOf(a.kind) - ['miss', 'plan', 'have'].indexOf(b.kind))

  const topPaid = useMemo(
    () =>
      vacancies.reduce((best, v) => ((salaryAverage(v) || 0) > (salaryAverage(best) || 0) ? v : best), vacancies[0]),
    [vacancies]
  )
  const focus = (activeVacancy && activeVacancy.id && vacancies.find((v) => v.id === activeVacancy.id)) || topPaid

  const pickVacancy = (v) => dispatch(setActiveVacancy(v))
  const pickProfession = (prof) => {
    dispatch(
      setActiveProfession({
        professionTitle: prof.name,
        procentague: prof.percent,
        tags: prof.tags,
        description: prof.description,
        id: prof.id,
      })
    )
    dispatch(setActiveVacancy(false))
  }

  // вакансию выбирают и здесь, и на диаграмме зарплат в верхней зоне — прокручиваем к ней в обоих случаях
  const prevVacancy = useRef(activeVacancy?.id)
  useEffect(() => {
    const id = activeVacancy?.id
    if (!id || id === prevVacancy.current) return
    prevVacancy.current = id
    setFlash((n) => n + 1)
    requestAnimationFrame(() => {
      if (!focusRef.current) return
      // верхняя зона шага 1 неподвижна — вакансия должна встать под ней, а не за ней
      const zone = document.querySelector('[data-gen-topzone]')
      const fixed = zone && getComputedStyle(zone).position === 'sticky' ? zone.offsetHeight : 0
      focusRef.current.style.scrollMarginTop = `${fixed + 12}px`
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [activeVacancy])

  // «В избранное» и «Описание» профессии живут в шапке «Зарплатных возможностей» (верхняя зона,
  // контейнер шага) — рисуем их туда порталом, состояние избранного остаётся здесь
  const profFavorite =
    activeProfession && (favoriteProfession || []).some((f) => String(f.profession?.id) === String(activeProfession.id))
  const [actionsSlot, setActionsSlot] = useState(null)
  useEffect(() => {
    setActionsSlot(document.getElementById(SALARY_ACTIONS_ID))
  }, [activeProfession])

  // вакансия в фокусе
  const focusChips = focus ? chipsFor(focus.skills) : []
  const focusMiss = focusChips.filter((c) => c.kind === 'miss').map((c) => c.skill)
  const matchOf = (v) => {
    const sk = (v.skills || []).filter((x) => x && x.name)
    if (!sk.length) return null
    return Math.round((sk.filter((x) => userHas(x.name)).length / sk.length) * 100)
  }
  const focusMatch = focus ? matchOf(focus) : null
  const focusFavorite = focus && (favoriteVacancy || []).some((f) => String(f.id) === String(focus.id))
  const sourceName = (v) => ((v?.alternate_url || '').includes('hh.ru') ? 'hh.ru' : 'trudvsem.ru')

  const query = q.trim().toLowerCase()
  const others = vacancies
    .filter((v) => !focus || v.id !== focus.id)
    .filter((v) => !query || (v.skills || []).some((x) => (x?.name || '').toLowerCase().includes(query)))
    .sort((a, b) => (salaryAverage(b) || 0) - (salaryAverage(a) || 0))
  const shownOthers = showAll ? others : others.slice(0, LIMIT_OTHERS)

  // пагинация профессий
  const total = (listProfessionOrigin || []).length
  const pages = Math.max(1, Math.ceil(total / PAGE))

  return (
    <section className={cn(s.stage, p.wrap)} aria-label="Обзор профессий">
      <div className={s.stagehead}>
        <div>
          <div className={s.kicker}>Шаг 1 из 3</div>
          <h2>Обзор профессий</h2>
          <div className={cn(s.muted, s.small)}>
            Выберите профессию — недостающие навыки добавляются в учебный план одним нажатием
          </div>
        </div>
        <div className={p.headTools}>
          <RegionSelect />
          <button
            type="button"
            className={cn(s.btn, s.ghost, s.sm)}
            aria-label={`Избранное: ${favoritesSum || 0}`}
            onClick={() => dispatch(setFlagFavorite(!flagModalFavorite))}
          >
            ★ Избранное · {favoritesSum || 0}
          </button>
          <button
            type="button"
            className={cn(s.btn, s.ghost, s.sm)}
            disabled={!activeProfession}
            title={activeProfession ? 'Фильтры вакансий' : 'Сначала выберите профессию'}
            onClick={() => dispatch(setFlagFilter(!flagModalFilter))}
          >
            Фильтры
          </button>
          <button type="button" className={s.linkbtn} onClick={() => changeIntro(true)}>
            Как это работает
          </button>
        </div>
      </div>

      <div className={p.cols}>
        {/* профессии */}
        <section className={cn(s.card, p.panel, p.left, 'gen-intro-professions')} aria-label="Профессии">
          <h3>Профессии</h3>
          <label className={s.field}>
            <SearchIcon />
            <input
              placeholder="Поиск профессии"
              aria-label="Поиск профессии"
              value={searchProfessionInput}
              onChange={(e) => setSearchProfessionInput(e.target.value)}
            />
          </label>
          <div className={p.profs}>
            {(displayListProfession || []).map((prof) => {
              const on = activeProfession && String(activeProfession.id) === String(prof.id)
              const fav = (favoriteProfession || []).some((f) => f.profession?.id === prof.id)
              const pct = prof.percent ? Math.round(prof.percent) : 0
              return (
                // плашка профессии как в прежнем списке: звезда | название | описание | круг совпадения
                <div
                  key={prof.id}
                  className={cn(p.prof, { [p.profOn]: on })}
                  role="button"
                  tabIndex={0}
                  aria-current={on ? 'true' : undefined}
                  onClick={() => pickProfession(prof)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      pickProfession(prof)
                    }
                  }}
                >
                  <button
                    type="button"
                    className={cn(p.star, { [p.starOn]: fav })}
                    aria-label={fav ? 'Убрать из избранного' : 'В избранное'}
                    aria-pressed={fav}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleChangeFavoriteProfession({
                        professionTitle: prof.name,
                        procentague: prof.percent,
                        tags: prof.tags,
                        id: prof.id,
                      })
                    }}
                  >
                    {fav ? '★' : '☆'}
                  </button>
                  <span className={p.profDiv} />
                  <span className={p.profName} lang="ru">{prof.name}</span>
                  <button
                    type="button"
                    className={p.info}
                    aria-label={`Описание профессии «${prof.name}»`}
                    onClick={(e) => {
                      e.stopPropagation()
                      pickProfession(prof)
                      openProfessionInfo()
                    }}
                  >
                    <InfoIcon />
                  </button>
                  <span className={p.ring} title="Совпадение ваших навыков с профессией">
                    <Progress
                      type="dashboard"
                      percent={pct}
                      gapDegree={1}
                      strokeColor={matchColor(pct)}
                      width={40}
                      strokeWidth={20}
                      format={(v) => v}
                    />
                  </span>
                </div>
              )
            })}
            {loadingProfession && <Spin style={{ margin: '24px auto' }} />}
            {!loadingProfession && !(displayListProfession || []).length && (
              <div className={cn(s.muted, s.small)} style={{ padding: 12 }}>
                {searchProfessionInput ? 'Ничего не нашлось' : 'Загружаем профессии…'}
              </div>
            )}
          </div>
          {total > PAGE && !searchProfessionInput && (
            <div className={p.pager}>
              <button
                type="button"
                className={cn(s.btn, s.ghost, s.sm)}
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                aria-label="Предыдущая страница"
              >
                ‹
              </button>
              <span>
                {currentPage} из {pages}
              </span>
              <button
                type="button"
                className={cn(s.btn, s.ghost, s.sm)}
                disabled={currentPage >= pages}
                onClick={() => setCurrentPage(currentPage + 1)}
                aria-label="Следующая страница"
              >
                ›
              </button>
            </div>
          )}
        </section>

        {/* профессия и вакансии */}
        <section className={p.right}>
          {!activeProfession ? (
            <div className={cn(s.card, s.empty)}>
              <h3 style={{ marginBottom: 6 }}>Выберите профессию слева</h3>
              Покажем зарплаты по вакансиям, ключевые навыки и чего вам не хватает
            </div>
          ) : (
            <>
              {actionsSlot &&
                createPortal(
                  <>
                    <button
                      type="button"
                      className={cn(s.btn, s.ghost, s.sm, { [s.on]: profFavorite })}
                      aria-pressed={!!profFavorite}
                      onClick={() =>
                        handleChangeFavoriteProfession({
                          professionTitle: activeProfession.professionTitle,
                          procentague: activeProfession.procentague,
                          tags: activeProfession.tags,
                          id: activeProfession.id,
                        })
                      }
                    >
                      {profFavorite ? '★ В избранном' : '☆ В избранное'}
                    </button>
                    <button type="button" className={cn(s.btn, s.ghost, s.sm)} onClick={openProfessionInfo}>
                      Описание
                    </button>
                  </>,
                  actionsSlot
                )}
              <div
                className={cn(s.card, p.panel, 'gen-intro-vacancies')}
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                <div className={p.rowBetween}>
                  <div>
                    <h3>Вакансии</h3>
                    <div className={cn(s.muted, s.small)}>Нажмите на навык с «+» — он попадёт в учебный план</div>
                  </div>
                  <label className={s.field} style={{ flex: '0 1 320px' }}>
                    <SearchIcon />
                    <input
                      placeholder="Вакансии с навыком, напр. Python"
                      aria-label="Найти вакансии по навыку"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </label>
                </div>

                {flagLoadingVacancy && !vacancies.length && <Spin style={{ margin: '24px auto' }} />}
                {!flagLoadingVacancy && !vacancies.length && (
                  <div className={s.empty}>Вакансий не нашлось — попробуйте другой город</div>
                )}

                {focus && (
                  <div
                    ref={focusRef}
                    key={`${focus.id}-${flash}`}
                    className={cn(p.vdetail, 'gen-intro-vacancy', { [p.flash]: flash > 0 })}
                  >
                    <div className={p.rowBetween} style={{ alignItems: 'flex-start' }}>
                      <div>
                        <div className={cn(s.muted, s.small)}>
                          {focus.employer?.name} · {salaryLabel(focus)}
                        </div>
                        <h3>{focus.name}</h3>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        {focusMatch !== null && (
                          <span className={cn(s.small, s.muted)}>
                            Совпадение <span className={s.pct}>{focusMatch}%</span>
                          </span>
                        )}
                        <button
                          type="button"
                          className={cn(p.star, { [p.starOn]: focusFavorite })}
                          aria-label={focusFavorite ? 'Убрать вакансию из избранного' : 'Вакансию в избранное'}
                          aria-pressed={!!focusFavorite}
                          onClick={() => handleChangeFavoriteVacancy(focus)}
                        >
                          {focusFavorite ? '★' : '☆'}
                        </button>
                        {focus.alternate_url && (
                          <a className={p.extlink} href={focus.alternate_url} target="_blank" rel="noopener noreferrer">
                            Открыть на {sourceName(focus)} →
                          </a>
                        )}
                      </div>
                    </div>
                    {focusChips.length > 0 ? (
                      <>
                        <div className={p.rowBetween}>
                          <div className={s.legend}>
                            <span>
                              <i style={{ background: 'var(--g-miss-text)' }} />
                              Не хватает — нажмите, чтобы добавить
                            </span>
                            <span>
                              <i style={{ background: 'var(--g-accent)' }} />В плане
                            </span>
                            <span>
                              <i style={{ background: 'var(--g-ok)' }} />
                              Уже есть
                            </span>
                          </div>
                          {focusMiss.length > 0 && (
                            <button
                              type="button"
                              className={cn(s.btn, s.sec, s.sm)}
                              onClick={() => addAllMissing(focusMiss)}
                            >
                              + Добавить все недостающие · {focusMiss.length}
                            </button>
                          )}
                        </div>
                        <div className={s.chips}>
                          {focusChips.map((c) => (
                            <SkillChip
                              key={c.skill.name}
                              skill={c.skill}
                              kind={c.kind}
                              onToggle={toggleSkill}
                              label={c.kind === 'plan' && lockedInPlan(c.skill.name)}
                            />
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className={cn(s.muted, s.small)}>Работодатель не указал навыки в этой вакансии</div>
                    )}
                  </div>
                )}

                {vacancies.length > 1 && (
                  <>
                    <span className={cn(s.muted, s.small)}>
                      {query
                        ? `Вакансии с навыком «${q.trim()}»: ${others.length}`
                        : `Другие вакансии · ${others.length} · по зарплате`}
                    </span>
                    <div className={p.vgrid}>
                      {shownOthers.map((v) => {
                        const chips = chipsFor(v.skills)
                        const more = chips.length - 4
                        const m = matchOf(v)
                        return (
                          <div
                            key={v.id}
                            className={p.vac}
                            role="button"
                            tabIndex={0}
                            aria-label={`Открыть вакансию «${v.name}»`}
                            onClick={() => pickVacancy(v)}
                            onKeyDown={(e) => {
                              if (e.target !== e.currentTarget) return
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                pickVacancy(v)
                              }
                            }}
                          >
                            <div className={p.rowBetween} style={{ flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                              <b>{v.name}</b>
                              {m !== null && <span className={s.pct}>{m}%</span>}
                            </div>
                            <div className={cn(s.muted, s.small)}>
                              {v.employer?.name} · {salaryLabel(v)}
                            </div>
                            {chips.length > 0 && (
                              <div className={s.chips} style={{ gap: 6 }}>
                                {chips.slice(0, 4).map((c) => (
                                  <SkillChip
                                    key={c.skill.name}
                                    skill={c.skill}
                                    kind={c.kind}
                                    small
                                    onToggle={toggleSkill}
                                    label={c.kind === 'plan' && lockedInPlan(c.skill.name)}
                                  />
                                ))}
                              </div>
                            )}
                            <span className={p.vlink}>
                              {more > 0 ? `Все навыки вакансии · ещё ${more} →` : 'Открыть вакансию →'}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                    {others.length > LIMIT_OTHERS && (
                      <button
                        type="button"
                        className={cn(s.btn, s.ghost, s.sm)}
                        style={{ alignSelf: 'center' }}
                        onClick={() => setShowAll(!showAll)}
                      >
                        {showAll ? 'Свернуть' : `Показать все вакансии · ${others.length}`}
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </section>
  )
}

export default ProfReview
